import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  adminApi,
  type DeviceSortField,
  type NewDeviceResponse,
  type OsType,
  type PlacementType,
  type DeviceListItem,
} from '../api/admin';
import { DataListFrame, sortArrow, useListControls } from '../components/data-list';

// 表示は日本語、DB保存値はコード（英語）のまま。enum 変更はしない。
const placementOptions: { value: PlacementType; label: string }[] = [
  { value: 'local_pc', label: 'PC(強力)' },
  { value: 'codespaces', label: 'codespace' },
  { value: 'vps_backup', label: 'vps' },
  { value: 'peer_pc', label: '軽量PC' },
];
const osOptions: { value: OsType; label: string }[] = [
  { value: 'windows', label: 'Windows' },
  { value: 'wsl2', label: 'WSL2' },
  { value: 'ubuntu', label: 'Ubuntu' },
  { value: 'codespaces', label: 'Codespaces' },
  { value: 'other', label: 'その他' },
];

const placementLabel = (v: PlacementType) => placementOptions.find((o) => o.value === v)?.label ?? v;
const osLabel = (v: OsType) => osOptions.find((o) => o.value === v)?.label ?? v;

// 並び替え可能列（backend DEVICE_SORT_FIELDS と一致）。
const SORT_OPTIONS: { value: DeviceSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: 'デバイス名' },
  { value: 'osType', label: 'OS' },
  { value: 'placementType', label: '配置' },
];

// デバイス画面の役割は「デバイス本体の登録/変更/削除」と死活・テスト送信まで。
// マウント(デバイスにマウントポイントを載せる)の追加/変更/削除は「デバイスマウント画面」(/settings/paths)へ集約した。
// ここでは一覧で、各デバイスに載っているマウントを要約表示するのみ。
export function DevicesPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['devices'] });

  const controls = useListControls<DeviceSortField>('konecco.list.devices', {
    q: '',
    sortBy: 'id',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // OS / 配置 はマスタ的（選択肢固定）= ドロップダウン絞り込み。'' は指定なし。
  const [osFilter, setOsFilter] = useState<'' | OsType>('');
  const [placementFilter, setPlacementFilter] = useState<'' | PlacementType>('');

  // 検索/並び替え/ページングは常にサーバ側（お手本: エージェント一覧）。死活表示のため 5秒ごとに再取得。
  const devicesQuery = useQuery({
    queryKey: [
      'devices',
      'search',
      controls.q,
      osFilter,
      placementFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      adminApi.searchDevices({
        q: controls.q || undefined,
        osType: osFilter || undefined,
        placementType: placementFilter || undefined,
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    refetchInterval: 5_000,
    placeholderData: keepPreviousData,
  });
  const pageItems = devicesQuery.data?.items ?? [];
  const total = devicesQuery.data?.total ?? 0;

  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeviceListItem | null>(null);
  const [createdSecret, setCreatedSecret] = useState<NewDeviceResponse | null>(null);
  const [tokenModal, setTokenModal] = useState<{ device: DeviceListItem; token: string } | null>(null);
  // WEB からの claude ログイン(ルートA): setup-token を貼って配布するモーダル。
  const [claudeLoginTarget, setClaudeLoginTarget] = useState<DeviceListItem | null>(null);

  // 編集対象は id で保持し、現在ページから都度引く（紐付け追加/解除を即座に反映）。
  const editTarget = editId != null ? pageItems.find((d) => d.id === editId) ?? null : null;

  const createDevice = useMutation({
    mutationFn: adminApi.createDevice,
    onSuccess: (res) => {
      setCreatedSecret(res);
      setCreateOpen(false);
      invalidate();
    },
  });

  const updateDevice = useMutation({
    mutationFn: (vars: {
      id: number;
      input: {
        name?: string;
        nickname?: string | null;
        tunnelHost?: string | null;
        placementType?: PlacementType;
        osType?: OsType;
        heartbeatEnabled?: boolean;
      };
    }) => adminApi.updateDevice(vars.id, vars.input),
    onSuccess: () => {
      setEditId(null);
      invalidate();
    },
  });

  const deleteDevice = useMutation({
    mutationFn: adminApi.deleteDevice,
    onSuccess: () => {
      setDeleteTarget(null);
      invalidate();
    },
  });
  const revokeJwt = useMutation({ mutationFn: adminApi.revokeDeviceJwt, onSuccess: invalidate });
  const unrevokeJwt = useMutation({ mutationFn: adminApi.unrevokeDeviceJwt, onSuccess: invalidate });
  const issueToken = useMutation({ mutationFn: adminApi.issueDeviceToken });
  const setClaudeToken = useMutation({
    mutationFn: (vars: { id: number; token: string }) =>
      adminApi.setClaudeToken(vars.id, vars.token),
    onSuccess: () => setTimeout(invalidate, 1500),
  });
  const broadcastClaudeToken = useMutation({
    mutationFn: (token: string) => adminApi.broadcastClaudeToken(token),
    onSuccess: () => setTimeout(invalidate, 1500),
  });
  // 個別「マスタ配布」(AI-WF #50): 鍵入力はせず、DB のマスタをこの1台へ適用+実測する。
  const applyMaster = useMutation({
    mutationFn: (id: number) => adminApi.applyMasterToDevice(id),
    onSuccess: () => setTimeout(invalidate, 1000),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 12 }}>
        デバイス（PC）本体の登録・変更・削除。マウント（どのマウントポイントを載せるか）の追加・変更は{' '}
        <Link to="/settings/paths">デバイスマウント画面</Link> に移動しました。
      </p>
      {devicesQuery.error && (
        <p className="k-error">取得に失敗しました: {(devicesQuery.error as Error).message}</p>
      )}

      <DataListFrame
        title="デバイス"
        total={total}
        loading={devicesQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（デバイス名・愛称・ログインID）"
        filters={
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                OS
              </span>
              <select
                value={osFilter}
                onChange={(e) => {
                  setOsFilter(e.target.value as '' | OsType);
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                {osOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                配置
              </span>
              <select
                value={placementFilter}
                onChange={(e) => {
                  setPlacementFilter(e.target.value as '' | PlacementType);
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                {placementOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <DeviceTable
          devices={pageItems}
          controls={controls}
          onEdit={(w) => setEditId(w.id)}
          onDelete={(w) => setDeleteTarget(w)}
          onRevoke={(id) => {
            if (confirm('このデバイスの接続キーを失効しますか？次回接続で拒否されます。')) {
              revokeJwt.mutate(id);
            }
          }}
          onUnrevoke={(id) => unrevokeJwt.mutate(id)}
          onToken={(w) =>
            issueToken.mutate(w.id, {
              onSuccess: (res) => setTokenModal({ device: w, token: res.token }),
            })
          }
          onClaudeLogin={(w) => setClaudeLoginTarget(w)}
          onApplyMaster={(w) => applyMaster.mutate(w.id)}
          applyingMaster={applyMaster.isPending}
        />
      </DataListFrame>

      {createOpen && (
        <DeviceFormDialog
          mode="create"
          submitting={createDevice.isPending}
          error={createDevice.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) =>
            createDevice.mutate({
              name: d.name,
              nickname: d.nickname,
              tunnelHost: d.tunnelHost,
              placementType: d.placementType,
              osType: d.osType,
              heartbeatEnabled: d.heartbeatEnabled,
            })
          }
        />
      )}

      {editTarget && (
        <DeviceFormDialog
          mode="edit"
          initial={editTarget}
          submitting={updateDevice.isPending}
          error={updateDevice.error as Error | null}
          onClose={() => setEditId(null)}
          onSubmit={(d) =>
            updateDevice.mutate({
              id: editTarget.id,
              input: {
                name: d.name,
                nickname: d.nickname,
                tunnelHost: d.tunnelHost,
                placementType: d.placementType,
                osType: d.osType,
                heartbeatEnabled: d.heartbeatEnabled,
              },
            })
          }
        />
      )}

      {deleteTarget && (
        <DeleteDeviceDialog
          device={deleteTarget}
          deleting={deleteDevice.isPending}
          error={deleteDevice.error as Error | null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteDevice.mutate(deleteTarget.id)}
        />
      )}

      {createdSecret && (
        <Modal title="登録完了 — デバイス 認証情報" width={560} onClose={() => setCreatedSecret(null)}>
          <p className="k-muted">
            この情報は <strong>本画面でしか確認できません</strong>。デバイスの{' '}
            <code>.env.local</code> にコピーしてください。
          </p>
          <pre
            style={{
              background: '#0f172a',
              color: '#e2e8f0',
              padding: 12,
              borderRadius: 8,
              overflow: 'auto',
            }}
          >
{`DEVICE_NAME=${createdSecret.name}
DEVICE_LOGIN_ID=${createdSecret.loginId}
DEVICE_LOGIN_PASSWORD=${createdSecret.password}`}
          </pre>
          <button className="btn-ghost" type="button" onClick={() => setCreatedSecret(null)}>
            閉じる
          </button>
        </Modal>
      )}

      {tokenModal && (
        <Modal
          title={`デバイス接続キー — #${tokenModal.device.id} ${tokenModal.device.name}`}
          width={640}
          onClose={() => setTokenModal(null)}
        >
          <p className="k-muted">
            この<strong>非失効トークン</strong>をワーカーの <code>.env.local</code> の{' '}
            <code>DEVICE_TOKEN</code> に貼ってください。<strong>再発行すると以前のトークンは無効</strong>になります。
          </p>
          <pre
            style={{
              background: '#0f172a',
              color: '#e2e8f0',
              padding: 12,
              borderRadius: 8,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
{`DEVICE_TOKEN=${tokenModal.token}`}
          </pre>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(tokenModal.token);
              }}
            >
              トークンをコピー
            </button>
            <button className="btn-ghost" type="button" onClick={() => setTokenModal(null)}>
              閉じる
            </button>
          </div>
        </Modal>
      )}

      {claudeLoginTarget && (
        <ClaudeLoginModal
          device={claudeLoginTarget}
          applying={setClaudeToken.isPending || broadcastClaudeToken.isPending}
          resultThis={setClaudeToken.data}
          resultAll={broadcastClaudeToken.data}
          error={(setClaudeToken.error || broadcastClaudeToken.error) as Error | null}
          onApplyThis={(token) =>
            setClaudeToken.mutate({ id: claudeLoginTarget.id, token })
          }
          onApplyAll={(token) => broadcastClaudeToken.mutate(token)}
          onClose={() => {
            setClaudeLoginTarget(null);
            setClaudeToken.reset();
            broadcastClaudeToken.reset();
          }}
        />
      )}
    </div>
  );
}

// ---- 汎用モーダル ----
function Modal({
  title,
  onClose,
  children,
  width = 460,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

interface DeviceFormValues {
  name: string;
  nickname: string | null;
  tunnelHost: string | null;
  placementType: PlacementType;
  osType: OsType;
  heartbeatEnabled: boolean;
}

// 新規作成 / 変更で共通。扱うのはデバイス本体の基本情報のみ（マウントはデバイスマウント画面で追加/変更）。
function DeviceFormDialog({
  mode,
  initial,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  mode: 'create' | 'edit';
  initial?: DeviceListItem;
  onSubmit: (v: DeviceFormValues) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [nickname, setNickname] = useState(initial?.nickname ?? '');
  const [tunnelHost, setTunnelHost] = useState(initial?.tunnelHost ?? '');
  const [placementType, setPlacementType] = useState<PlacementType>(
    initial?.placementType ?? 'local_pc',
  );
  const [osType, setOsType] = useState<OsType>(initial?.osType ?? 'wsl2');
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(initial?.heartbeatEnabled ?? true);

  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;

  return (
    <Modal
      title={mode === 'create' ? '新しいデバイスを登録' : `デバイスを変更 — #${initial?.id} ${initial?.name}`}
      onClose={onClose}
      width={mode === 'edit' ? 620 : 480}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSubmit({
            name: name.trim(),
            nickname: nickname.trim() || null,
            tunnelHost: tunnelHost.trim() || null,
            placementType,
            osType,
            heartbeatEnabled,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={fieldWrap}>
          <label style={labelStyle}>名前（ホスト名・例: omni-pc-wsl）</label>
          <input
            placeholder="例: omni-pc-wsl"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>愛称（任意・例: デル）— あれば優先表示</label>
          <input
            placeholder="例: デル"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>VS Code Tunnel 名（任意・例: asus-appuser）</label>
          <input
            placeholder="例: asus-appuser"
            value={tunnelHost}
            onChange={(e) => setTunnelHost(e.target.value)}
          />
          <span style={{ fontSize: 11, color: '#64748b' }}>
            ホスト名とは別物なので手入力する。空にすると未登録に戻る。
          </span>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>配置</label>
          <select
            value={placementType}
            onChange={(e) => setPlacementType(e.target.value as PlacementType)}
          >
            {placementOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>OS</label>
          <select value={osType} onChange={(e) => setOsType(e.target.value as OsType)}>
            {osOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={heartbeatEnabled}
            onChange={(e) => setHeartbeatEnabled(e.target.checked)}
          />
          ハートビート有効（HB）
        </label>

        {error && <p className="k-error">{error.message}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !name.trim()}>
            {mode === 'create' ? '登録' : '変更を保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// GitHub 風: 対象の name を打ち込むまで削除ボタンを無効化する破壊的操作の確認。
function DeleteDeviceDialog({
  device,
  onConfirm,
  onClose,
  deleting,
  error,
}: {
  device: DeviceListItem;
  onConfirm: () => void;
  onClose: () => void;
  deleting: boolean;
  error: Error | null;
}) {
  const [text, setText] = useState('');
  const match = text === device.name;
  return (
    <Modal title="デバイスを削除" onClose={onClose} width={520}>
      <p style={{ marginTop: 0 }}>
        この操作は <strong style={{ color: '#b91c1c' }}>取り消せません</strong>。
        デバイス <code>{device.name}</code> (#{device.id}) と、それに紐づく以下が削除されます:
      </p>
      <ul style={{ fontSize: 13, color: '#475569', marginTop: 0 }}>
        <li>登録済みマウント {device.deviceProjectMap.length} 件</li>
        <li>SSH コンフィグ・オフラインキューの保留分</li>
        <li>過去の request ログ / チャット履歴は残りますが、このデバイスへの紐付けは失われます</li>
      </ul>
      <p style={{ fontSize: 13 }}>
        確認のため、デバイス名 <code>{device.name}</code> を入力してください:
      </p>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={device.name}
        autoFocus
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {error && <p className="k-error">{error.message}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn-ghost" type="button" onClick={onClose}>
          キャンセル
        </button>
        <button
          className="btn-danger"
          type="button"
          disabled={!match || deleting}
          onClick={onConfirm}
        >
          このデバイスを完全に削除
        </button>
      </div>
    </Modal>
  );
}

interface DeviceTableProps {
  devices: DeviceListItem[];
  controls: ReturnType<typeof useListControls<DeviceSortField>>;
  onEdit: (w: DeviceListItem) => void;
  onDelete: (w: DeviceListItem) => void;
  onRevoke: (id: number) => void;
  onUnrevoke: (id: number) => void;
  onToken: (device: DeviceListItem) => void;
  onClaudeLogin: (device: DeviceListItem) => void;
  onApplyMaster: (device: DeviceListItem) => void;
  applyingMaster: boolean;
}

function DeviceTable(props: DeviceTableProps) {
  const { devices, controls } = props;
  if (devices.length === 0) {
    return (
      <p className="k-muted">
        まだデバイスが登録されていません。右上の「＋ 新規作成」から追加してください。
      </p>
    );
  }
  // 列ヘッダ（タイトル行）クリックで並び替え（▲▼）。
  const sortable = { cursor: 'pointer', userSelect: 'none' } as const;
  return (
    <table className="k-table">
      <thead>
        <tr>
          <th style={sortable} onClick={() => controls.toggleSort('id')} title="クリックで並び替え">
            ID{sortArrow(controls, 'id')}
          </th>
          <th style={sortable} onClick={() => controls.toggleSort('name')} title="クリックで並び替え">
            デバイス{sortArrow(controls, 'name')}
          </th>
          <th style={sortable} onClick={() => controls.toggleSort('osType')} title="クリックで並び替え">
            OS / 配置{sortArrow(controls, 'osType')}
          </th>
          <th>死活(HB)</th>
          <th>状態</th>
          <th title="その端末で Claude Code(CLI) が使えるか。トークン(worker接続)とは別レイヤ">
            Claude Code
          </th>
          <th>マウント</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        {devices.map((w) => (
          <DeviceRow key={w.id} device={w} {...props} />
        ))}
      </tbody>
    </table>
  );
}

// 1 デバイス = 1 行。載っているマウント（マウントポイント名）を要約表示する（読み取り専用）。
// マウントの一覧・追加・変更・削除は「デバイスマウント画面」(/settings/paths)に集約。ここからはリンクで誘導する。
function DeviceRow({
  device,
  onEdit,
  onDelete,
  onRevoke,
  onUnrevoke,
  onToken,
  onApplyMaster,
  applyingMaster,
}: { device: DeviceListItem } & Omit<DeviceTableProps, 'devices'>) {
  const mounts = device.deviceProjectMap;
  // 有効/無効で分けて要約する。7個マウントして4個無効、のような状態が一覧で一目で分かるように。
  const enabledMounts = mounts.filter((p) => p.enabled);
  const disabledMounts = mounts.filter((p) => !p.enabled);

  return (
    <tr>
      <td style={td}>{device.id}</td>
      <td style={td}>
        <strong>{device.nickname || device.name}</strong>
        {device.nickname && (
          <span className="k-muted" style={{ marginLeft: 6, fontSize: 12 }}>
            （{device.name}）
          </span>
        )}
      </td>
      <td style={td}>
        {osLabel(device.osType)} / {placementLabel(device.placementType)}
      </td>
      <td style={td}>{device.heartbeatEnabled ? formatHb(device.lastHeartbeatAt) : '—'}</td>
      <td style={td}>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 999,
            background: device.online ? '#dcfce7' : '#fef2f2',
            color: device.online ? '#166534' : '#991b1b',
            fontSize: 12,
          }}
        >
          {device.online ? 'オンライン' : 'オフライン'}
        </span>
        {device.jwtRevoked && (
          <span
            style={{
              marginLeft: 6,
              padding: '2px 8px',
              borderRadius: 999,
              background: '#fef3c7',
              color: '#92400e',
              fontSize: 12,
            }}
          >
            接続キー失効中
          </span>
        )}
      </td>
      <td style={td}>
        <ClaudeCodeCell device={device} />
      </td>
      <td style={td}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          {mounts.length === 0 ? (
            <span
              style={{
                display: 'inline-block',
                background: '#fef2f2',
                color: '#b91c1c',
                borderRadius: 8,
                padding: '2px 8px',
                fontSize: 12,
                fontWeight: 600,
              }}
              title="マウントが1件も割り当てられていません（未割り当て）"
            >
              未割り当て
            </span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              <span className="k-muted" style={{ fontSize: 11 }}>
                有効 {enabledMounts.length}
                {disabledMounts.length > 0 && ` / 無効 ${disabledMounts.length}`}
              </span>
              {enabledMounts.map((p) => (
                <span
                  key={p.id}
                  style={{
                    background: '#eef2ff',
                    color: '#3730a3',
                    borderRadius: 8,
                    padding: '1px 7px',
                    fontSize: 12,
                  }}
                >
                  {p.mountPoint.name}
                </span>
              ))}
              {disabledMounts.map((p) => (
                <span
                  key={p.id}
                  style={{
                    background: '#f1f5f9',
                    color: '#94a3b8',
                    borderRadius: 8,
                    padding: '1px 7px',
                    fontSize: 12,
                    textDecoration: 'line-through',
                  }}
                  title="無効（実行対象から外れている）"
                >
                  {p.mountPoint.name}
                </span>
              ))}
            </div>
          )}
          <Link
            to="/settings/paths"
            style={{ fontSize: 12, color: '#1d4ed8' }}
            title="デバイスマウント画面で、このデバイスのマウントを追加・変更・削除します"
          >
            デバイスマウント（{mounts.length}）→
          </Link>
        </div>
      </td>
      <td style={td}>
        {/* 操作は2段。上段=ふだん使う設定系、下段=接続停止・削除の注意系。 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn-ghost"
              type="button"
              style={opBtn}
              onClick={() => onEdit(device)}
              title="デバイスの名前・愛称・OS・配置などを変更します"
            >
              変更
            </button>
            <button
              className="btn-ghost"
              type="button"
              style={opBtn}
              onClick={() => onToken(device)}
              title="このデバイスのワーカーを Konecco に接続するための鍵を発行します（ワーカーの .env.local の DEVICE_TOKEN に貼る）"
            >
              デバイス接続キー
            </button>
            <button
              className="btn-ghost"
              type="button"
              style={opBtn}
              disabled={applyingMaster}
              onClick={() => onApplyMaster(device)}
              title="Claude 認証マスタ（設定→マスタ管理）の現在の鍵を、このデバイスに配布・適用して実認証を確認します。鍵の設定はマスタ管理で行います。"
            >
              {applyingMaster ? 'マスタ配布中…' : 'マスタ配布'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {device.jwtRevoked ? (
              <button
                className="btn-ghost"
                type="button"
                style={opBtn}
                onClick={() => onUnrevoke(device.id)}
                title="失効を解除し、このデバイスの再接続を許可します"
              >
                デバイス接続キー復活
              </button>
            ) : (
              <button
                className="btn-warn"
                type="button"
                style={opBtn}
                onClick={() => onRevoke(device.id)}
                title="このデバイスの接続キーを失効します（次回接続から拒否。紛失・誤接続時に使う）"
              >
                デバイス接続キー失効
              </button>
            )}
            <button
              className="btn-danger"
              type="button"
              style={opBtn}
              onClick={() => onDelete(device)}
              title="このデバイスを Konecco から完全に削除します"
            >
              削除
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// Claude Code(CLI) 利用可否を2段で表示: ①インストール/版 ②認証(ログイン+アカウント)。
// worker JWT(トークン)とは別レイヤ。version は `claude --version`、
// auth は `claude auth status`(課金ゼロ) 由来。run の成否でも補助的に更新される。
function ClaudeCodeCell({ device }: { device: DeviceListItem }) {
  const badge = (bg: string, color: string, text: string, title?: string) => (
    <span
      title={title}
      style={{ padding: '2px 8px', borderRadius: 999, background: bg, color, fontSize: 12 }}
    >
      {text}
    </span>
  );

  // 【2026-06-12】オフライン中は claude の"現在の"認証状態を確認できない。
  // 古い cli-health を緑「認証OK」で出すと「オフラインなのに認証OK」という嘘になるため、
  // オフライン時は中立(灰色)で「最終オンライン時の情報」と明示する。
  if (!device.online) {
    return badge(
      '#f1f5f9',
      '#64748b',
      device.claudeCliVersion
        ? `${device.claudeCliVersion}（オフライン・未確認）`
        : 'オフライン（未確認）',
      'オフライン中は claude の認証状態を確認できません。表示は最終オンライン時の情報です。オンライン復帰時に再確認されます。',
    );
  }

  // まだ一度も cli-health を受け取っていない (旧worker / 起動直後)。
  if (device.claudeCliInstalled === null || device.claudeCliInstalled === undefined) {
    return badge('#f1f5f9', '#64748b', '未確認', 'ワーカーからまだ報告が来ていません');
  }
  if (device.claudeCliInstalled === false) {
    return badge('#fef2f2', '#991b1b', '未インストール', 'この端末に claude が見つかりません (ENOENT / PATH)');
  }

  // インストール済み: 多次元の実測ステータス (AI-WF #50)。
  // 認証は実測プローブ(claudeAuthProbe)を優先表示。鍵=マスタ一致 / DIR / 最終実測時刻も出す。
  const AUTH: Record<string, { t: string; bg: string; c: string }> = {
    ok: { t: '認証OK', bg: '#dcfce7', c: '#166534' },
    invalid_token: { t: '不正トークン(401)', bg: '#fef2f2', c: '#991b1b' },
    not_logged_in: { t: '未ログイン', bg: '#fef2f2', c: '#991b1b' },
    expired: { t: '期限切れ', bg: '#fef2f2', c: '#991b1b' },
    rate_limited: { t: 'レート制限', bg: '#fef9c3', c: '#854d0e' },
    api_error: { t: 'API異常', bg: '#fef9c3', c: '#854d0e' },
    no_cli: { t: 'claude無し', bg: '#fef2f2', c: '#991b1b' },
    unknown: { t: '認証不明', bg: '#fef9c3', c: '#854d0e' },
  };
  const probe = device.claudeAuthProbe ?? null;
  const ai = probe
    ? AUTH[probe] ?? AUTH.unknown
    : device.claudeAuthStatus === 'ok'
      ? AUTH.ok
      : device.claudeAuthStatus === 'unauthenticated'
        ? AUTH.not_logged_in
        : { t: '認証未確認', bg: '#f1f5f9', c: '#64748b' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {badge('#eef2ff', '#3730a3', device.claudeCliVersion || '導入済み')}
        {badge(ai.bg, ai.c, ai.t, probe ? '実走 claude で確認した実測結果' : '実測未取得(配布/最新確認で更新)')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {device.claudeKeyMatch === true
          ? badge('#dcfce7', '#166534', '鍵:マスタ一致')
          : device.claudeKeyMatch === false
            ? badge('#fef2f2', '#991b1b', '鍵:不一致', 'この端末の鍵がマスタと違う。「マスタ配布」で揃う')
            : null}
        {device.claudeDirOk === true
          ? badge('#f1f5f9', '#475569', 'DIR✓')
          : device.claudeDirOk === false
            ? badge('#fef2f2', '#991b1b', 'DIR不在', '割当マウントの一部/全部が端末に存在しない')
            : null}
      </div>
      {probe === 'ok' && device.claudeAuthEmail && (
        <span className="k-muted" style={{ fontSize: 11 }} title="ログイン中のアカウント">
          {device.claudeAuthEmail}
          {device.claudeAuthPlan && ` (${device.claudeAuthPlan})`}
        </span>
      )}
      {device.claudeProbedAt && (
        <span className="k-muted" style={{ fontSize: 10 }} title="最後に実測プローブした時刻">
          実測{' '}
          {new Date(device.claudeProbedAt).toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      )}
    </div>
  );
}

// WEB からの claude ログイン(ルートA)。手順を知らなくても完了できるよう誘導する:
// ①自分のPCで `claude setup-token` を実行(コピー) → ②出たトークンを貼る → ③この端末/全端末に配布。
// トークンは Backend に保存されず、各 worker の env(CLAUDE_CODE_OAUTH_TOKEN)に入って永続化される。
function ClaudeLoginModal({
  device,
  applying,
  resultThis,
  resultAll,
  error,
  onApplyThis,
  onApplyAll,
  onClose,
}: {
  device: DeviceListItem;
  applying: boolean;
  resultThis?: { delivered: boolean; reason?: string };
  resultAll?: { total: number; deliveredCount: number };
  error: Error | null;
  onApplyThis: (token: string) => void;
  onApplyAll: (token: string) => void;
  onClose: () => void;
}) {
  const [token, setToken] = useState('');
  const trimmed = token.trim();
  const looksValid = trimmed.length > 20;
  // 未確認 = この端末のワーカーがまだ cli-health を返していない（旧ワーカー）。
  // 旧ワーカーは set-claude-token を理解できず黙って無視するため、配布しても無意味。
  const isStale = device.claudeCliInstalled === null || device.claudeCliInstalled === undefined;

  return (
    <Modal
      title={`Claude ログイン — #${device.id} ${device.nickname || device.name}`}
      width={680}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className="k-muted" style={{ margin: 0 }}>
          各端末を触らず、<strong>WEBから一括でログイン</strong>します。サブスク(Max等)の長期トークンを
          1個作って配るだけ。各端末を回る必要はありません。
        </p>

        {isStale && (
          <div
            style={{
              background: '#fef9c3',
              border: '1px solid #fde047',
              color: '#854d0e',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
            }}
          >
            ⚠️ この端末は「未確認」（まだ新ワーカーが入っていません）。
            <strong>このままトークンを配っても、この端末は受け取れません</strong>
            （旧ワーカーは無視します）。先にこの端末のワーカーを新コードへ更新してください。
            ※ デル(開発機)は更新済みなので、まずデルで試せます。
          </div>
        )}

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            ① 自分のPCのターミナルでこれを実行（1回だけ）
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code
              style={{
                flex: 1,
                background: '#0f172a',
                color: '#e2e8f0',
                padding: '8px 12px',
                borderRadius: 8,
                fontSize: 14,
              }}
            >
              claude setup-token
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText('claude setup-token')}
            >
              コピー
            </button>
          </div>
          <p className="k-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
            ブラウザで1回ログインすると <code>claude_code_oauth_token_…</code> が出ます（約1年有効）。
          </p>
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            ② 出てきたトークンをここに貼り付け
          </div>
          <textarea
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="claude_code_oauth_token_..."
            spellCheck={false}
            style={{
              width: '100%',
              minHeight: 70,
              fontFamily: 'monospace',
              fontSize: 13,
              padding: 10,
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>③ 配布先を選んで適用</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={!looksValid || applying || isStale}
              onClick={() => onApplyThis(trimmed)}
              title={
                isStale
                  ? 'この端末は新ワーカー未導入のため配布できません'
                  : 'この端末（オンライン時のみ）に配布'
              }
            >
              この端末に適用（{device.nickname || device.name}）
            </button>
            <button
              type="button"
              disabled={!looksValid || applying}
              onClick={() => onApplyAll(trimmed)}
              title="テナント内の全端末に一括配布（オンライン端末のみ届く）"
            >
              全端末に一括適用
            </button>
            <button className="btn-ghost" type="button" onClick={onClose}>
              閉じる
            </button>
          </div>
        </div>

        {applying && <p className="k-muted" style={{ margin: 0 }}>配布中…</p>}
        {error && <p className="k-error" style={{ margin: 0 }}>失敗: {error.message}</p>}
        {resultThis && (
          <p style={{ margin: 0, color: resultThis.delivered ? '#166534' : '#991b1b' }}>
            {resultThis.delivered
              ? '✓ この端末に配布しました。数秒後に列が「認証OK」に変わります。'
              : `✗ 配布できませんでした（${resultThis.reason ?? 'オフライン'}）。端末がオンラインの時に再実行してください。`}
          </p>
        )}
        {resultAll && (
          <p style={{ margin: 0, color: resultAll.deliveredCount > 0 ? '#166534' : '#991b1b' }}>
            ✓ 全{resultAll.total}台中 {resultAll.deliveredCount}台に配布しました（残りはオフライン）。
            数秒後に各列が更新されます。
          </p>
        )}
      </div>
    </Modal>
  );
}

function formatHb(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}秒前`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}分前`;
  return date.toLocaleString();
}

const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
const opBtn = { padding: '2px 10px', fontSize: 12, whiteSpace: 'nowrap' } as const;
